import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Upload, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { BookingDialog } from "@/components/BookingDialog";
import { QuickPhone } from "@/components/QuickPhone";
import { api, errMsg, fmtDate } from "@/lib/api";

function IcalDialog({ open, onOpenChange, onDone }) {
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState("");
  const [file, setFile] = useState(null);
  useEffect(() => {
    if (open) api.get("/rooms").then(({ data }) => setRooms(data));
  }, [open]);
  const go = async () => {
    const fd = new FormData();
    fd.append("room_id", room);
    fd.append("file", file);
    try {
      const { data } = await api.post("/ical/import", fd);
      toast.success(`Importate ${data.created} prenotazioni (${data.skipped} saltate: già presenti o sovrapposte${data.blocked ? `; ${data.blocked} date bloccate Airbnb ignorate` : ""})`);
      onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ical-dialog">
        <DialogHeader><DialogTitle>Importa file iCal (Booking, Airbnb)</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-500">Vengono importate solo le date: i dati degli ospiti arrivano dal check-in online. Per la sincronizzazione automatica imposta l'URL iCal della camera in Impostazioni → Camere.</p>
        <div className="space-y-1.5">
          <Label>Camera</Label>
          <Select value={room || undefined} onValueChange={setRoom}>
            <SelectTrigger data-testid="ical-room-select"><SelectValue placeholder="Seleziona camera" /></SelectTrigger>
            <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <input type="file" accept=".ics,text/calendar" onChange={(e) => setFile(e.target.files?.[0])} data-testid="ical-file-input" className="text-sm" />
        <DialogFooter><Button disabled={!room || !file} onClick={go} data-testid="ical-import-button">Importa</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Bookings() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "all";
  const [items, setItems] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [icalOpen, setIcalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);
  const load = useCallback(() => {
    api.get("/bookings", { params: { view } }).then(({ data }) => setItems(data)).catch((e) => toast.error(errMsg(e)));
  }, [view]);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Prenotazioni</h1>
          <p className="text-stone-500 text-sm mt-1">Crea, importa e segui il check-in di ogni soggiorno.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIcalOpen(true)} data-testid="open-ical-import-button"><Upload className="h-4 w-4 mr-2" />Importa iCal</Button>
          <Button onClick={() => setNewOpen(true)} data-testid="new-booking-button"><Plus className="h-4 w-4 mr-2" />Nuova</Button>
        </div>
      </div>
      <Tabs value={view} onValueChange={(v) => setParams({ view: v })}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="all" data-testid="bookings-tab-all">Tutte</TabsTrigger>
          <TabsTrigger value="arrivi" data-testid="bookings-tab-arrivi">Arrivi oggi</TabsTrigger>
          <TabsTrigger value="partenze" data-testid="bookings-tab-partenze">Partenze oggi</TabsTrigger>
          <TabsTrigger value="incompleti" data-testid="bookings-tab-incompleti">Check-in incompleti</TabsTrigger>
          <TabsTrigger value="cancellate" data-testid="bookings-tab-cancellate">Cancellate</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="grid gap-3" data-testid="bookings-list">
        {items.length === 0 && <p className="text-stone-500 text-sm">Nessuna prenotazione in questa vista.</p>}
        {items.map((b) => (
          <div key={b.id} className="rounded-xl border bg-white hover:border-primary transition-colors">
          <Link to={`/prenotazioni/${b.id}`} className="flex flex-wrap items-center justify-between gap-3 p-4 pb-2" data-testid={`booking-row-${b.id}`}>
            <div className="min-w-0">
              <p className="font-semibold">{b.ref_name || "Senza nome"} <span className="text-xs font-normal text-stone-400">· {b.source}</span></p>
              <p className="text-sm text-stone-500">{b.room_name} · {fmtDate(b.arrival)} → {fmtDate(b.departure)}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-sm text-stone-500"><Users className="h-4 w-4" />{b.guests_filled}/{b.guests_count}</span>
              <StatusBadge kind="booking" status={b.status} />
            </div>
          </Link>
          <div className="px-4 pb-3"><QuickPhone booking={b} templates={templates} /></div>
          </div>
        ))}
      </div>
      <BookingDialog open={newOpen} onOpenChange={setNewOpen} onSaved={load} />
      <IcalDialog open={icalOpen} onOpenChange={setIcalOpen} onDone={load} />
    </div>
  );
}
